import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { getTranslation } from "../services/translations";
import { fadeUp, fadeIn, staggerContainer, slideLeft, slideRight, scaleIn, viewportOpts } from "../utils/animations";
import "./LandingPage.css";

const IMGS = ["/3.png","/1.png","/2.png","/5.png","/4.png"].map(p=>`${process.env.PUBLIC_URL}${p}`);
const CheckSVG = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>);
const ChevSVG = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="11" height="11"><polyline points="6 9 12 15 18 9"/></svg>);

export default function LandingPage() {
  const { language, setLanguage } = useLanguage();
  const t = k => getTranslation(language, k);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [openDD, setOpenDD] = useState(null);
  const [tIdx, setTIdx] = useState(0);
  const navRef = useRef(null);

  useEffect(() => { const id = setInterval(() => setHeroIdx(i => (i+1)%IMGS.length), 5000); return () => clearInterval(id); }, []);
  useEffect(() => { const id = setInterval(() => setTIdx(i => (i+1)%TESTS.length), 4000); return () => clearInterval(id); }, []);
  useEffect(() => { const fn = () => setScrolled(window.scrollY > 60); window.addEventListener("scroll", fn); return () => window.removeEventListener("scroll", fn); }, []);
  useEffect(() => { const fn = () => { if (window.innerWidth > 960) setMenuOpen(false); }; window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);
  useEffect(() => { const fn = e => { if (navRef.current && !navRef.current.contains(e.target)) setOpenDD(null); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);

  const go = id => { setMenuOpen(false); setOpenDD(null); const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior:"smooth", block:"start" }); };

  const NAV = [
    { label: t("home"), action: () => go("home") },
    { label: t("aboutUs2"), dd: [
      { label: t("aboutUs2"), desc: t("ourStoryMission"), to: "/about" },
      { label: t("directorMessage"), desc: t("fromDrAshok"), to: "/director-message" },
    ]},
    { label: t("services"), wide: true, dd: [
      { label: t("endoscopy"), desc: t("upperGIExam"), to: "/services/endoscopy" },
      { label: t("colonoscopy"), desc: t("colonScreening"), to: "/services/colonoscopy" },
      { label: t("ercp"), desc: t("bileDuctProc"), to: "/services/ercp" },
      { label: t("biopsy"), desc: t("tissueSampling"), to: "/services/biopsy" },
      { label: t("varicealLigation"), desc: t("bleedingControl"), to: "/services/varicealLigation" },
      { label: t("foreignBodyRemoval"), desc: t("safeRetrieval"), to: "/services/foreignBodyRemoval" },
      { label: t("enteralMetalStenting"), desc: t("stentTherapy"), to: "/services/enteralMetalStenting" },
      { label: t("pegPlacement"), desc: t("feedingTube"), to: "/services/pegPlacement" },
      { label: t("polypectomy"), desc: t("polypRemoval"), to: "/services/polypectomy" },
      { label: t("hemoclipApplication"), desc: t("bleedingClipping"), to: "/services/hemoclipApplication" },
      { label: t("estUlcer"), desc: t("ulcerTreatment"), to: "/services/estUlcer" },
      { label: t("strictureDilatation"), desc: t("narrowingCorrection"), to: "/services/strictureDilatation" },
      { label: t("allServicesArrow"), action: () => go("services") },
    ]},
    { label: t("teamLabel"), to: "/team" },
    { label: t("facilities"), wide: true, dd: [
      { label: t("facilityOTTitle"), desc: t("advancedSurgical"), to: "/facilities/ot" },
      { label: t("facilityConsultTitle"), desc: t("privateConsult"), to: "/facilities/consult" },
      { label: t("facilityWaitingTitle"), desc: t("patientLounge"), to: "/facilities/waiting" },
      { label: t("facilityEmergencyTitle"), desc: t("emergencyCare247"), to: "/facilities/emergency" },
      { label: t("facilityReceptionTitle"), desc: t("patientReg"), to: "/facilities/reception" },
      { label: t("facilityLabTitle"), desc: t("roundClockTesting"), to: "/facilities/lab" },
      { label: t("facilityPharmacyTitle"), desc: t("medicinesAvail"), to: "/facilities/pharmacy" },
      { label: t("facilityParkingTitle"), desc: t("freeParkingDesc"), to: "/facilities/parking" },
      { label: t("allFacilitiesArrow"), action: () => go("facilities") },
    ]},
    { label: t("contact"), action: () => go("contact") },
  ];

  const SVCS = [
    { key:"endoscopy",           img:"Endoscopy.png" },
    { key:"colonoscopy",         img:"Colonoscopy.png" },
    { key:"ercp",                img:"ERCP.png" },
    { key:"biopsy",              img:"Biopsy.png" },
    { key:"varicealLigation",    img:"variceal-ligation.png" },
    { key:"foreignBodyRemoval",  img:"foreign-body-removal.png" },
    { key:"enteralMetalStenting",img:"enteral-metal-stenting.png" },
    { key:"pegPlacement",        img:"peg-placement.png" },
    { key:"polypectomy",         img:"Polypectomy.png" },
    { key:"hemoclipApplication", img:"hemoclip-application.png" },
    { key:"estUlcer",            img:"est-of-ulcer.png" },
    { key:"strictureDilatation", img:"stricture-dilatation.png" },
  ];

  const FACS = [
    { key:"ot", tk:"facilityOTTitle", dk:"facilityOTDesc", img:`${process.env.PUBLIC_URL}/1.png` },
    { key:"consult", tk:"facilityConsultTitle", dk:"facilityConsultDesc", img:`${process.env.PUBLIC_URL}/4.png` },
    { key:"waiting", tk:"facilityWaitingTitle", dk:"facilityWaitingDesc", img:`${process.env.PUBLIC_URL}/3.png` },
    { key:"emergency", tk:"facilityEmergencyTitle", dk:"facilityEmergencyDesc", img:`${process.env.PUBLIC_URL}/5.png` },
    { key:"reception", tk:"facilityReceptionTitle", dk:"facilityReceptionDesc", img:`${process.env.PUBLIC_URL}/7f.png` },
    { key:"lab", tk:"facilityLabTitle", dk:"facilityLabDesc", img:`${process.env.PUBLIC_URL}/8l.png` },
    { key:"pharmacy", tk:"facilityPharmacyTitle", dk:"facilityPharmacyDesc", img:`${process.env.PUBLIC_URL}/6p.png` },
    { key:"parking", tk:"facilityParkingTitle", dk:"facilityParkingDesc", img:`${process.env.PUBLIC_URL}/9P.png` },
  ];

  const ACHIEVEMENTS = [
    { img: `${process.env.PUBLIC_URL}/ach1.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach2.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach3.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach4.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach5.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach6.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach7.jpeg` },
    { img: `${process.env.PUBLIC_URL}/ach8.jpeg` },
  ];

  const [achIdx, setAchIdx] = useState(0);

  useEffect(() => { const id = setInterval(() => setAchIdx(i => (i+1)%ACHIEVEMENTS.length), 3000); return () => clearInterval(id); }, []);

  const TESTS = [
    { name: "Rishi Yadav", r: 5, time: "2 months ago", text: "Dr. Ashok is a very good gastro doctor in Narnaul. He listens patiently and explains the problem and treatment clearly. The diagnosis felt accurate, and the treatment helped a lot. He is calm, respectful, and does not recommend unnecessary tests. Overall a very positive experience. Highly recommended." },
    { name: "Deepak Sharma", r: 5, time: "a year ago", text: "Dr Ashok Kumar is one of the best Gastro consultants in Haryana, Narnaul. Lowest OPD charge in Narnaul. He is a specialist for liver, pancreas and intestines. Laboratory available, and second OPD available for general medicine. Dr Ashok Kumar is one of the best Gastro consultants in Narnaul, Haryana. For other information please visit Gastro Liver Centre near Narnaul bus stand." },
    { name: "Praveen Kumar", r: 5, time: "2 months ago", text: "This is a good hospital for liver care. Dr. Ashok Ji's nature is very good. He's very simple. All the staff are also good. The most important thing is the medicine — all the medicines are standard, none are generic. Very good 👍" },
  ];

  return (
    <div className="landing-page">
      <div className="top-bar">
        <div className="top-bar-inner">
          <div className="top-bar-left">
            <a href="/book" className="top-bar-btn">{t("bookEAppointment")}</a>
            <span className="top-bar-divider"/>
            <span className="top-bar-emergency">{t("emergencyColon")} <strong>+91 9996504991</strong></span>
          </div>
          <div className="top-bar-right">
            <a href="https://www.facebook.com/people/Ashok-Gastro-Liver-Centre/61569483430200/#" target="_blank" rel="noopener noreferrer" className="top-social" aria-label="Facebook"><svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg></a>
            <a href="https://www.instagram.com/dr.ashok.gastro/" target="_blank" rel="noopener noreferrer" className="top-social" aria-label="Instagram"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></a>
            <a href="https://www.linkedin.com/in/dr-ashok-kumar-761733275/" target="_blank" rel="noopener noreferrer" className="top-social" aria-label="LinkedIn"><svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg></a>
            <span className="top-bar-divider"/>
            <div className="lang-switcher">
              <button className={`lang-btn${language==="en"?" active":""}`} onClick={()=>setLanguage("en")}>EN</button>
              <button className={`lang-btn${language==="hi"?" active":""}`} onClick={()=>setLanguage("hi")}>HI</button>
            </div>
          </div>
        </div>
      </div>

      <div className="logo-header">
        <div className="logo-header-inner">
          <div className="logo-section">
            <div className="logo-icon"><img src={`${process.env.PUBLIC_URL}/logo1.png`} alt="Logo" className="logo-img"/></div>
            <div className="logo-text-inline">
              <span className="logo-main-text">{t("hospitalName")}</span>
              <span className="logo-sub-text">{t("hospitalTagline")}</span>
            </div>
          </div>
          <div className="header-contact-strip">
            <div className="header-contact-item">
              <div className="hci-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></div>
              <div><span className="hci-label">{t("callUs")}</span><span className="hci-val">+91 9996504991</span></div>
            </div>
            <div className="header-contact-item">
              <div className="hci-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
              <div><span className="hci-label">{t("emergency")}</span><span className="hci-val">{t("available247")}</span></div>
            </div>
            <div className="header-contact-item">
              <div className="hci-icon-wrap"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
              <div><span className="hci-label">{t("locationNav")}</span><span className="hci-val">{t("narnaulHaryana")}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className={`navbar-wrapper${scrolled?" scrolled":""}`} ref={navRef}>
        <nav className="navbar">
          <ul className="nav-menu">
            {NAV.map((item, idx) => (
              <li key={idx} className={item.dd?"nav-item-has-dropdown":""}>
                {item.dd ? (
                  <>
                    <button className={`nav-link${openDD===idx?" active":""}`} onClick={()=>setOpenDD(p=>p===idx?null:idx)}>
                      {item.label} <span className="nav-chevron"><ChevSVG/></span>
                    </button>
                    {openDD===idx && (
                      <div className={`nav-dropdown${item.wide?" nav-dd-wide":""}`}>
                        {item.dd.map((s,si)=>s.to
                          ? <Link key={si} to={s.to} className="nav-dropdown-item" onClick={()=>setOpenDD(null)}>
                              <span className="nav-dd-label">{s.label}</span>
                              {s.desc && <span className="nav-dd-desc">{s.desc}</span>}
                            </Link>
                          : <button key={si} className="nav-dropdown-item" onClick={()=>{s.action();setOpenDD(null);}}>
                              <span className="nav-dd-label">{s.label}</span>
                              {s.desc && <span className="nav-dd-desc">{s.desc}</span>}
                            </button>
                        )}
                      </div>
                    )}
                  </>
                ) : item.to ? (
                  <Link to={item.to} className="nav-link">{item.label}</Link>
                ) : (
                  <button className="nav-link" onClick={item.action}>{item.label}</button>
                )}
              </li>
            ))}
            <li><Link to="/book" className="nav-book-btn">{t("bookAppointment")}</Link></li>
          </ul>
          <button className="hamburger" onClick={()=>setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <span style={{transform:menuOpen?"rotate(45deg) translate(5px,5px)":"none"}}/>
            <span style={{opacity:menuOpen?0:1}}/>
            <span style={{transform:menuOpen?"rotate(-45deg) translate(5px,-5px)":"none"}}/>
          </button>
        </nav>
        {menuOpen && (
          <div className="mobile-nav">
            <button className="nav-link" onClick={()=>go("home")}>{t("home")}</button>
            <Link to="/about" className="nav-link" onClick={()=>setMenuOpen(false)}>{t("aboutUs2")}</Link>
            <Link to="/director-message" className="nav-link" onClick={()=>setMenuOpen(false)}>{t("directorMessage")}</Link>
            <button className="nav-link" onClick={()=>{go("services");setMenuOpen(false);}}>{t("allServicesArrow")}</button>
            <Link to="/team" className="nav-link" onClick={()=>setMenuOpen(false)}>{t("teamLabel")}</Link>
            <button className="nav-link" onClick={()=>{go("facilities");setMenuOpen(false);}}>{t("facilities")}</button>
            <button className="nav-link" onClick={()=>{go("contact");setMenuOpen(false);}}>{t("contact")}</button>
            <Link to="/book" className="nav-book-btn" onClick={()=>setMenuOpen(false)}>{t("bookAppointment")}</Link>
          </div>
        )}
      </div>

      <section id="home" className="hero-section">
        {IMGS.map((img,i)=>(
          <div key={i} className={`hero-slide${i===heroIdx?" active":""}`} style={{backgroundImage:`url(${img})`}}/>
        ))}
        <div className="hero-overlay"/>
        <motion.div
          className="hero-content-wrap"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeUp} className="hero-accred-row">
            <span className="hero-accred-pill"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>15+ Years Experience</span>
          </motion.div>
          <motion.h1 variants={fadeUp} className="hero-title">
            {t("heroTitlePart1")} <span className="hero-highlight">{t("heroTitleHighlight")}</span> {t("heroTitlePart2")}<br/>
            <span className="hero-title-white">{t("heroTitlePart3")}</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="hero-desc">{t("welcomeText")}</motion.p>
          <motion.div variants={fadeUp} className="hero-stats-row">
            <div className="hero-stat"><span className="hs-num">15+</span><span className="hs-lbl">{t("yearsExperience")}</span></div>
            <div className="hero-stat-div"/>
            <div className="hero-stat"><span className="hs-num">1L+</span><span className="hs-lbl">{t("patientsTreated")}</span></div>
            <div className="hero-stat-div"/>
            <div className="hero-stat"><span className="hs-num">24/7</span><span className="hs-lbl">{t("emergencyStatLbl")}</span></div>
          </motion.div>
          <motion.div variants={fadeUp} className="hero-cta-row">
            <Link to="/book"><button className="btn-primary">{t("bookAppointment")}</button></Link>
            <a href="tel:+919996504991"><button className="btn-outline-white">Call Now</button></a>
          </motion.div>
        </motion.div>
        <div className="hero-book-float">
          <div className="hbf-title">{t("quickAppointment")}</div>
          <Link to="/book" className="hbf-btn">{t("bookEAppointmentBtn")}</Link>
          <div className="hbf-emergency">{t("emergencyColon")} <strong>+91 9996504991</strong></div>
        </div>
        <div className="hero-indicators">
          {IMGS.map((_,i)=>(<button key={i} className={`hero-dot${i===heroIdx?" active":""}`} onClick={()=>setHeroIdx(i)} aria-label={`Slide ${i+1}`}/>))}
        </div>
      </section>

      <div className="trust-strip">
        <div className="trust-strip-inner">
          <div className="trust-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg><span>{t("mdDmGastroTrust")}</span></div>
          <div className="trust-div"/>
          <div className="trust-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg><span>{t("emergency247Services")}</span></div>
          <div className="trust-div"/>
          <div className="trust-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg><span>{t("patientRating")}</span></div>
        </div>
      </div>

      <section className="achievements-section">
        <div className="achievements-carousel-wrapper">
          <div className="achievements-carousel-track">
            {[...ACHIEVEMENTS, ...ACHIEVEMENTS].map((ach, i) => (
              <div key={i} className="achievement-carousel-item">
                <div className="achievement-carousel-card">
                  <img src={ach.img} alt="Achievement" className="ach-image"/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="about-section">
        <motion.div
          className="about-inner"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOpts}
        >
          <motion.div variants={slideLeft} className="about-img-col">
            <div className="about-img-frame">
              <img src={`${process.env.PUBLIC_URL}/dr.png`} alt="Dr. Ashok Kumar" className="about-doc-img"/>
              <div className="about-badge-card"><span className="abc-num">15+</span><span className="abc-lbl">{t("yearsOfExcellenceBadge")}</span></div>
            </div>
          </motion.div>
          <motion.div variants={slideRight} className="about-text-col">
            <div className="section-eyebrow">{t("aboutUsEyebrow")}</div>
            <h2 className="section-title">{t("trustedNameLanding")}<br/><span className="title-accent">{t("gastroCareLanding")}</span></h2>
            <p className="about-para">{t("landingAboutPara1")}</p>
            <p className="about-para">{t("landingAboutPara2")}</p>
            <div className="about-stats-row">
              <div className="about-stat"><span className="ast-num">15+</span><span className="ast-lbl">Years Experience</span></div>
              <div className="about-stat-div"/>
              <div className="about-stat"><span className="ast-num">1L+</span><span className="ast-lbl">{t("patientsTreatedStat")}</span></div>
              <div className="about-stat-div"/>
              <div className="about-stat"><span className="ast-num">24/7</span><span className="ast-lbl">{t("emergencyStatLbl")}</span></div>
            </div>
            <div className="about-features">
              <div className="af-item"><CheckSVG/><span>{t("advancedEndoscopy")}</span></div>
              <div className="af-item"><CheckSVG/><span>{t("emergency24ICU")}</span></div>
              <div className="af-item"><CheckSVG/><span>{t("inhouseLab")}</span></div>
              <div className="af-item"><CheckSVG/><span>{t("compassionateCare")}</span></div>
            </div>
            <div className="about-cta-row">
              <Link to="/book"><button className="btn-primary">{t("bookAppointment")}</button></Link>
              <Link to="/about"><button className="btn-outline-blue">{t("learnMore")}</button></Link>
            </div>
          </motion.div>
        </motion.div>
      </section>

      <section id="services" className="services-section">
        <div className="section-header">
          <div className="section-eyebrow">{t("ourServices")}</div>
          <h2 className="section-title">{t("ourServices")}</h2>
          <p className="section-subtitle">{t("servicesSubtitle")}</p>
        </div>
        <motion.div
          className="services-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOpts}
        >
          {SVCS.map((s,i)=>(
            <motion.div
              key={i}
              variants={scaleIn}
              className="service-card"
              onClick={()=>navigate(`/services/${s.key}`)}
              role="button"
              tabIndex={0}
              onKeyDown={e=>e.key==="Enter"&&navigate(`/services/${s.key}`)}
              style={{backgroundImage:`url(${process.env.PUBLIC_URL}/${s.img})`}}
            >
              <div className="service-card-overlay"/>
              <div className="service-card-content">
                <h3>{t(s.key)}</h3>
                <p className="service-card-desc">{t(`${s.key}Description`).substring(0,70)}...</p>
                <span className="service-card-link">{t("viewDetails")} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><polyline points="9 18 15 12 9 6"/></svg></span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section id="facilities" className="facilities-section">
        <div className="section-header">
          <div className="section-eyebrow">{t("ourFacilities")}</div>
          <h2 className="section-title">{t("ourFacilities")}</h2>
          <p className="section-subtitle">{t("facilitiesSubtitle")}</p>
        </div>
        <div className="facilities-grid">
          {FACS.map((f,i)=>(
            <div key={i} className="facility-card" onClick={()=>navigate(`/facilities/${f.key}`)} role="button" tabIndex={0} onKeyDown={e=>e.key==="Enter"&&navigate(`/facilities/${f.key}`)}>
              <div className="facility-img-wrapper">
                <img src={f.img} alt={t(f.tk)} className="facility-img"/>
                <div className="facility-img-overlay"><span className="facility-view-btn">{t("viewDetails")}</span></div>
              </div>
              <div className="facility-info">
                <h4>{t(f.tk)}</h4>
                <p>{t(f.dk)}</p>
                <span className="facility-link">{t("learnMoreLink")} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><polyline points="9 18 15 12 9 6"/></svg></span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="testimonials-section">
        <div className="section-header">
          <div className="section-eyebrow">{t("patientTestimonialsEyebrow")}</div>
          <h2 className="section-title">{t("whatPatientsSay")}</h2>
          <a href="https://www.google.com/maps/search/Dr+Ashok+Gastro+Liver+Hospital+Narnaul" target="_blank" rel="noopener noreferrer" className="google-review-link">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t("viewAllGoogleReviews")}
          </a>
        </div>
        <div className="testimonials-carousel">
          <div className="testimonials-track" style={{transform:`translateX(-${tIdx*100}%)`}}>
            {TESTS.map((t2,i)=>(
              <div key={i} className="testimonial-slide">
                <div className="testimonial-card">
                  <div className="tc-stars">
                    {[...Array(5)].map((_,si)=>(<svg key={si} viewBox="0 0 24 24" fill={si<t2.r?"#f4c430":"none"} stroke="#f4c430" strokeWidth="1.5" width="16" height="16"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>))}
                  </div>
                  <p className="tc-text">"{t2.text}"</p>
                  <div className="tc-footer">
                    <div className="tc-avatar">{t2.name.charAt(0)}</div>
                    <div><div className="tc-name">{t2.name}</div><div className="tc-source">{t("googleReview")}</div><div className="tc-time">{t2.time}</div></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="testimonial-controls">
            <button className="tc-prev" onClick={()=>setTIdx(i=>(i-1+TESTS.length)%TESTS.length)} aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><polyline points="15 18 9 12 15 6"/></svg></button>
            <div className="tc-dots">{TESTS.map((_,i)=>(<button key={i} className={`tc-dot${i===tIdx?" active":""}`} onClick={()=>setTIdx(i)} aria-label={`Review ${i+1}`}/>))}</div>
            <button className="tc-next" onClick={()=>setTIdx(i=>(i+1)%TESTS.length)} aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><polyline points="9 18 15 12 9 6"/></svg></button>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-text">
            <h2>{t("readyBookTitle")}</h2>
            <p>{t("readyBookDesc")}</p>
          </div>
          <div className="cta-actions">
            <Link to="/book"><button className="cta-btn-primary">{t("bookAppointment")}</button></Link>
            <a href="tel:+919996504991"><button className="cta-btn-outline">+91 9996504991</button></a>
          </div>
        </div>
      </section>

      <section id="contact" className="contact-section">
        <div className="section-header">
          <div className="section-eyebrow">{t("contactUs")}</div>
          <h2 className="section-title">{t("contactUs")}</h2>
        </div>
        <div className="contact-area">
          <div className="contact-details">
            <div className="contact-card">
              <div className="contact-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
              <div className="contact-card-body"><h4>{t("Address")}</h4><p>{t("addressDetails")}</p></div>
            </div>
            <div className="contact-card">
              <div className="contact-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg></div>
              <div className="contact-card-body"><h4>{t("Phone")}</h4><p>{t("phoneDetails")}</p></div>
            </div>
            <div className="contact-card">
              <div className="contact-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></div>
              <div className="contact-card-body"><h4>{t("Email")}</h4><p>{t("emailDetails")}</p></div>
            </div>
            <div className="contact-card">
              <div className="contact-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
              <div className="contact-card-body"><h4>{t("Hours")}</h4><p>{t("hoursDetails")}</p></div>
            </div>
          </div>
          <div className="contact-map">
            <iframe title="clinic-location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3509.5!2d76.1!3d28.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d5a1b1b1b1b1b%3A0x1b1b1b1b1b1b1b1b!2sShree%20Nath%20Plaza%2C%20Rewari%20Rd%2C%20Narnaul%2C%20Haryana%20123001!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin" width="100%" height="100%" style={{border:0}} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"/>
          </div>
        </div>
        <div className="contact-btn-wrap">
          <a href="https://www.google.com/maps?q=Shree+Nath+Plaza,+Rewari+Road,+Narnaul,+Haryana+123001" target="_blank" rel="noopener noreferrer">
            <button className="btn-primary">{t("getDirections")}</button>
          </a>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-grid">
          <div className="footer-brand footer-col">
            <div className="footer-logo-wrap">
              <div className="footer-logo-icon"><img src={`${process.env.PUBLIC_URL}/logo1.png`} alt="Logo" style={{width:"100%",height:"100%",objectFit:"contain"}}/></div>
              <div className="logo-text-group">
                <span className="logo-main" style={{color:"#fff"}}>{t("footerBrandName")}</span>
                <span className="logo-sub" style={{color:"rgba(255,255,255,0.6)"}}>{t("footerBrandSub2")}</span>
              </div>
            </div>
            <p className="footer-tagline">{t("hospitalDescription")}</p>
            <div className="footer-contact-inline">
              <div className="footer-contact-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg><span>+91 9996504991</span></div>
              <div className="footer-contact-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg><span>drashokgastrolivercentre@gmail.com</span></div>
              <div className="footer-contact-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg><span>{t("footerCity")}</span></div>
            </div>
          </div>
          <div className="footer-col">
            <h4>{t("quickLinks")}</h4>
            <ul className="footer-links">
              <li><button className="footer-link-btn" onClick={()=>go("home")}>{t("home")}</button></li>
              <li><Link to="/about" className="footer-link-a">{t("footerAboutUs")}</Link></li>
              <li><button className="footer-link-btn" onClick={()=>go("services")}>{t("services")}</button></li>
              <li><Link to="/team" className="footer-link-a">{t("teamLabel")}</Link></li>
              <li><button className="footer-link-btn" onClick={()=>go("facilities")}>{t("facilities")}</button></li>
              <li><button className="footer-link-btn" onClick={()=>go("contact")}>{t("contact")}</button></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/people/Ashok-Gastro-Liver-Centre/61569483430200/#" target="_blank" rel="noopener noreferrer" className="social-icon social-facebook" aria-label="Facebook"><svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg></a>
              <a href="https://www.instagram.com/dr.ashok.gastro/" target="_blank" rel="noopener noreferrer" className="social-icon social-instagram" aria-label="Instagram"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></a>
              <a href="https://www.linkedin.com/in/dr-ashok-kumar-761733275/" target="_blank" rel="noopener noreferrer" className="social-icon social-linkedin" aria-label="LinkedIn"><svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg></a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">2025 Dr. Ashok Gastro and Liver Hospital. {t("allRightsReserved")}</p>
          <div className="footer-legal">
            <a href="#privacy">{t("privacyPolicy")}</a>
            <a href="#terms">{t("termsOfService")}</a>
            <a href="#disclaimer">{t("disclaimer")}</a>
          </div>
        </div>
      </footer>

    </div>
  );
}